import { saveAs } from "file-saver";
import { TFunction } from 'i18next';

// Ol
import OlGeoJSON from 'ol/format/GeoJSON';
import Feature from "ol/Feature";
import Geometry from "ol/geom/Geometry";

import moment, { Moment } from 'moment';
import DataControllerSubModel from '@/lib/DataControllerSubModel';

import radio from '@/models/submodels/radio';
import checkbox from '@/models/submodels/checkbox';
import picker from '@/models/submodels/picker';

import { formats } from '@/lib/formats';


// Types
import { PickerItem } from '@/@types/controls/controls';
import { IFieldPickerModel, FieldAny, PickerItemValue } from '@/@types/models/model';
import { ISubModelsWithData } from '@/@types/ui/Table';
import { DCFieldValue, DCRecord } from '@/@types/lib/dataController';
import { IDataControllerSub } from '@/@types/lib/dataControllerSub';

interface ExportShpOptions {
  records: DCRecord[];
  fields: Array<FieldAny>;
  filename: string;
  t: TFunction;
}

async function exportShp({ records, fields, filename, t }: ExportShpOptions) {
  if (!records?.length) return;

  const geoJSONFormatter = new OlGeoJSON();
  // eslint-disable-next-line global-require
  const shpwrite = require("@/lib/shp-write");

  const fieldsWithModel = fields.filter(
    (f) =>
      ['picker', 'checkbox', 'radio', 'boolean', 'active', 'iconStatus'].includes(f.type) &&
      Object.prototype.hasOwnProperty.call(f, 'subModel')
  ) as Array<IFieldPickerModel>;

  const fieldSubModels: ISubModelsWithData = {};
  const fieldPromises = fieldsWithModel.map((f) => {
    const modelType = f.type === 'picker' ? picker : f.type === 'radio' ? radio : checkbox;
    const newSubModel = new DataControllerSubModel(modelType, f);
    fieldSubModels[f.source] = newSubModel;
    return newSubModel.GetData();
  });

  await Promise.all(fieldPromises).catch(err => console.error('Error loading submodels:', err));

  const features = records.map(rec => {
    let geometry;
    if (rec.geom) {
      const olGeom = (rec.geom as Feature<Geometry>).getGeometry();
      // geometry = olGeom ? geoJSONFormatter.writeGeometryObject(olGeom.clone().transform("EPSG:3857", "EPSG:3765")) : { type: "Point", coordinates: [0, 0] };
      geometry = olGeom ? geoJSONFormatter.writeGeometryObject(olGeom.clone()) : { type: "Point", coordinates: [0, 0] };
    } else {
      geometry = { type: "Point", coordinates: [0, 0] };
    }

    return {
      type: "Feature",
      geometry,
      properties: fields.reduce((props, f) => {
        const val = rec[f.source];
        const sModel = fieldSubModels[f.source] || null;
        // eslint-disable-next-line no-param-reassign
        props[f.title] = getData(f, sModel, val, t);
        return props;
      }, {} as Record<string, any>),
    };
  });

  const wkt3857 = `PROJCS["WGS 84 / Pseudo-Mercator",
    GEOGCS["WGS 84",
        DATUM["WGS_1984",
            SPHEROID["WGS 84",6378137,298.257223563,
                AUTHORITY["EPSG","7030"]],
            AUTHORITY["EPSG","6326"]],
        PRIMEM["Greenwich",0,
            AUTHORITY["EPSG","8901"]],
        UNIT["degree",0.0174532925199433,
            AUTHORITY["EPSG","9122"]],
        AUTHORITY["EPSG","4326"]],
    PROJECTION["Mercator_1SP"],
    PARAMETER["central_meridian",0],
    PARAMETER["scale_factor",1],
    PARAMETER["false_easting",0],
    PARAMETER["false_northing",0],
    UNIT["metre",1,
        AUTHORITY["EPSG","9001"]],
    AXIS["X",EAST],
    AXIS["Y",NORTH],
    AUTHORITY["EPSG","3857"]]`;

  const geoJSON = {
    type: "FeatureCollection",
    features,
    crs: {
      type: "name",
      properties: { name: "EPSG:3857" },
    },
  };

  try {
    const shp = await shpwrite.zip(geoJSON, {
      folder: `${filename}`,
      types: {
        point: `${filename}-points`,
        multipoint: `${filename}-points`,
        line: `${filename}-lines`,
        polyline: `${filename}-lines`,
        polygon: `${filename}-polygons`,
        multipolygon: `${filename}-polygons`
      },
      outputType: "blob",
      prj: wkt3857
    });
    saveAs(shp, `${filename}_${Date.now()}.zip`);
  } catch (error) {
    console.error("Error creating SHP zip:", error);
  }
}

function getData(field: FieldAny, sModel: IDataControllerSub | null, val: DCFieldValue, t: TFunction): string {
  if (val === null || undefined === null) return '';
  const { type } = field;
  switch (type) {
    case 'date':
      return val
        ? moment
            .utc(val as Moment)
            .local()
            .format(formats.date)
        : '';
    case 'datetime':
      return val
        ? moment
            .utc(val as Moment)
            .local()
            .format(formats.datetime)
        : '';
    case 'boolean':
    case 'radio': {
      if (sModel) {
        if (val === null || val === undefined) {
          return '';
        }
        const rec = sModel.records.find((x) => x.value === val);
        return rec ? t(rec.label) : '';
      }
      const { items } = field;
      if (items) {
        const ind = items.values.indexOf(val as PickerItemValue);
        const valueLabel = t(items.labels[ind]);
        return valueLabel;
      }
      return val as string;
    }
    case 'checkbox':
      if (Number.isInteger(val)) {
        if (sModel) {
          const rec = sModel.records.find((x) => x.value === val);
          return rec ? t(rec.label) : '';
        }
        const { items } = field;
        if (!items) return '';
        const codes = val.toString(2).split('').reverse();
        // return codes.map((x,i) => t(items.labels[items.values.indexOf(Math.pow(2,i))])).join(', ');
        const { labels, values } = items;
        return codes
          .map((x, i) => {
            const ind = values.indexOf(2 ** i);
            if (!ind) return null;
            return t(labels[ind]);
          })
          .filter((x) => x !== null)
          .join(', ');
      }
      if (Array.isArray(val)) {
        if (sModel) {
          return val
            .map((x) => {
              const rec = sModel.records.find((f) => f.value === x /* || f.value === x.value */);
              return rec ? t(rec.label) : '';
            })
            .filter((v) => v !== '')
            .join(', ');
        }
        return (val as Array<PickerItem>).map((x) => x.value).join(', ');
      }
      return `${val}`;

    case 'picker': {
      if (sModel) {
        if (val === null || val === undefined) {
          return '';
        }
        const rec = sModel.records.find((x) => x.value === val);
        return rec ? t(rec.label) : '';
      }
      const { items } = field;
      if (items) {
        const ind = items.values.indexOf(val as string);
        const valueLabel = t(items.labels[ind]);
        return valueLabel;
      }
      return `${val}`;
    }
    default:
      break;
  }
  return String(val);
}

export default exportShp;